import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PDFDocument, rgb } from 'pdf-lib';
import { Spinner, Alert } from 'react-bootstrap';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
    FaSignature,
    FaTimes,
    FaDownload,
    FaCheckCircle,
    FaExclamationTriangle,
    FaFileContract,
    FaPalette,
    FaCalendarAlt,
    FaEraser
} from 'react-icons/fa';
import ContractEmployeService from '../../../services/ContractEmployeService';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';

// ── Lecteur PDF en lecture seule, chargé en blob puis rendu en canvas (pdf.js) ─
//    Contourne le blocage d'affichage en <iframe> des URLs presignées MinIO
//    (X-Frame-Options / CSP frame-ancestors), et fonctionne aussi sur mobile.
const PdfReader = ({ url }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(320);
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setPdfLoading(true); setPdfError(false); setBlobUrl(null); setNumPages(0);

        const load = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(objectUrl);
            } catch (e) {
                console.error('Erreur de lecture du document PDF :', e);
                if (!cancelled) { setPdfError(true); setPdfLoading(false); }
            }
        };
        if (url) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setPageWidth(containerRef.current.clientWidth - 16);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [blobUrl]);

    if (pdfError) {
        return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 4 }}>
                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <Typography variant="h6">Aperçu indisponible</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Ce document PDF n'a pas pu être chargé pour lecture.
                </Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f1f3f5', px: 1, py: 2 }}>
            {(pdfLoading || !blobUrl) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Chargement du document…</Typography>
                </Box>
            )}
            {blobUrl && (
                <Document
                    file={blobUrl}
                    onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPdfLoading(false); }}
                    onLoadError={(e) => { console.error('Erreur pdf.js :', e); setPdfError(true); setPdfLoading(false); }}
                    loading={null}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        {Array.from({ length: numPages }, (_, i) => (
                            <Box key={i} sx={{ boxShadow: '0 1px 6px rgba(0,0,0,0.15)', background: '#fff' }}>
                                <Page pageNumber={i + 1} width={pageWidth > 0 ? pageWidth : 320} />
                            </Box>
                        ))}
                    </Box>
                </Document>
            )}
        </Box>
    );
};

const AdminSignatureModal = ({ contract, isOpen, onClose, onSuccess }) => {
    const [pdfUrl, setPdfUrl] = useState('');
    const [modifiedPdfUrl, setModifiedPdfUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');

    const sigCanvas = useRef(null);
    const canvasWrapperRef = useRef(null);
    const localBlobUrlRef = useRef(null);

    // ─────────────────────────────────────────────────────────────────────────
    // FIX PRINCIPAL : synchronise les dimensions internes du canvas <canvas>
    // avec sa taille d'affichage CSS, éliminant le décalage de coordonnées.
    // ─────────────────────────────────────────────────────────────────────────
    const syncCanvasSize = useCallback(() => {
        if (!sigCanvas.current || !canvasWrapperRef.current) return;

        const canvas = sigCanvas.current.getCanvas();
        const wrapper = canvasWrapperRef.current;
        const rect = wrapper.getBoundingClientRect();

        const newWidth = Math.floor(rect.width);
        const newHeight = Math.floor(rect.height);

        if (canvas.width !== newWidth || canvas.height !== newHeight) {
            const imageData = canvas.width > 0 && canvas.height > 0
                ? canvas.toDataURL()
                : null;

            canvas.width = newWidth;
            canvas.height = newHeight;

            if (imageData) {
                const img = new Image();
                img.onload = () => {
                    const ctx = canvas.getContext('2d');
                    if (ctx) ctx.drawImage(img, 0, 0, newWidth, newHeight);
                };
                img.src = imageData;
            }
        }
    }, []);

    useEffect(() => {
        if (!canvasWrapperRef.current) return;

        const observer = new ResizeObserver(() => {
            syncCanvasSize();
        });

        observer.observe(canvasWrapperRef.current);
        syncCanvasSize();

        return () => {
            observer.disconnect();
        };
    }, [syncCanvasSize, activeTab]);

    const resetSignature = () => {
        sigCanvas.current?.clear();
        setError(null);
        setTimeout(syncCanvasSize, 50);
    };

    const getCurrentUser = () => {
        return localStorage.getItem('username') || 'admin';
    };

    const formatDateTimeForDisplay = () => {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}`;
    };

    // Initialisation à l'ouverture : on affiche la DERNIÈRE version signée par
    // l'employé, via le lien presigné MinIO.
    useEffect(() => {
        if (isOpen && contract?.contractDocEmployeSignedPresigned) {
            setPdfUrl(contract.contractDocEmployeSignedPresigned);
            resetSignature();
            setModifiedPdfUrl(null);
            setError(null);
            setSuccess(false);
            setActiveTab('preview');
        }

        return () => {
            if (localBlobUrlRef.current) {
                URL.revokeObjectURL(localBlobUrlRef.current);
                localBlobUrlRef.current = null;
            }
        };
    }, [isOpen, contract]);

    useEffect(() => {
        if (activeTab === 'signature') {
            const timer = setTimeout(syncCanvasSize, 100);
            return () => clearTimeout(timer);
        }
    }, [activeTab, syncCanvasSize]);

    const generateFileName = (contract) => {
        const contractId = contract.id;
        const employeName = getFullName(contract.employe?.user).replace(/\s+/g, '-');
        const timestamp = Date.now();
        return `CONTRACT-EMPLOYE-${contractId}-${employeName}-ACTIVE-${timestamp}.pdf`;
    };

    const getFullName = (user) => {
        if (!user) return 'EMPLOYE';
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'EMPLOYE';
    };

    const handleSaveSignature = async () => {
        if (sigCanvas.current.isEmpty()) {
            setError('Veuillez fournir votre signature avant de continuer');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // 1. Récupérer le PDF déjà signé par l'employé, via le lien presigné
            const pdfResponse = await fetch(contract.contractDocEmployeSignedPresigned);
            if (!pdfResponse.ok) {
                throw new Error('Impossible de récupérer le PDF signé par l\'employé');
            }
            const pdfBytes = await pdfResponse.arrayBuffer();

            const signatureUrl = sigCanvas.current.toDataURL('image/png');
            const signatureRes = await fetch(signatureUrl);
            const signatureBytes = await signatureRes.arrayBuffer();

            const pdfDoc = await PDFDocument.load(pdfBytes);
            const pages = pdfDoc.getPages();

            if (pages.length < 2) {
                throw new Error('Le document doit avoir au moins 2 pages');
            }

            const secondPage = pages[1];
            const { width } = secondPage.getSize();

            const signatureImage = await pdfDoc.embedPng(signatureBytes);

            const signatureWidth = 150;
            const signatureHeight = 50;
            const marginBottom = 180;
            const marginRight = 50;

            // Signature admin à droite
            secondPage.drawImage(signatureImage, {
                x: width - signatureWidth - marginRight,
                y: marginBottom,
                width: signatureWidth,
                height: signatureHeight
            });

            secondPage.drawText('Signature DHS', {
                x: width - signatureWidth - marginRight,
                y: marginBottom - 15,
                size: 10,
                color: rgb(0, 0, 0)
            });

            const displayDateTime = formatDateTimeForDisplay();
            secondPage.drawText(`Date: ${displayDateTime}`, {
                x: width - signatureWidth - marginRight,
                y: marginBottom - 30,
                size: 9,
                color: rgb(0, 0, 0)
            });

            const modifiedPdfBytes = await pdfDoc.save();

            // 2. Construire le fichier signé et l'envoyer directement au backend
            const fileName = generateFileName(contract);
            const signedPdfBlob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
            const signedPdfFile = new File([signedPdfBlob], fileName, { type: 'application/pdf' });

            const username = getCurrentUser();
            const result = await ContractEmployeService.signByAdmin(contract.id, username, signedPdfFile);

            // 3. Déterminer l'URL à afficher : on privilégie la réponse backend
            const backendSignedUrl =
                result?.contractEmployeActivePresigned ||
                result?.contractEmployeActive ||
                null;

            let displayUrl;
            if (backendSignedUrl) {
                displayUrl = `${backendSignedUrl}${backendSignedUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
            } else {
                const localUrl = URL.createObjectURL(signedPdfBlob);
                localBlobUrlRef.current = localUrl;
                displayUrl = localUrl;
            }

            setModifiedPdfUrl(displayUrl);
            setSuccess(true);
            setActiveTab('result');
            resetSignature();

            if (onSuccess) onSuccess(result);

        } catch (err) {
            console.error('Erreur lors de la signature DHS:', err);
            setError(err.message || 'Une erreur est survenue lors de la signature');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadSignedPdf = () => {
        if (modifiedPdfUrl) {
            const link = document.createElement('a');
            link.href = modifiedPdfUrl;
            link.download = `contrat-employe-${contract.id}-active.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (!isOpen || !contract) return null;

    return (
        <>
            {/* Overlay et modal principale */}
            <div
                className="admin-signature-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="admin-signature-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', height: '95vh', display: 'flex', flexDirection: 'column',
                        animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        flex: '0 0 auto', position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                    <FaSignature className="text-primary" size={24} />
                                    Finalisation et activation du contrat employé
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    Référence: {contract.contractRef}
                                </p>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    {!success && (
                        <div className="px-4 pt-4" style={{ flex: '0 0 auto' }}>
                            <ul className="nav nav-tabs mb-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'preview' ? 'active text-success' : 'text-secondary'}`}
                                        onClick={() => setActiveTab('preview')}
                                        style={{ fontWeight: 500, border: 'none', background: 'none', padding: '10px 20px' }}
                                    >
                                        <FaFileContract className="me-2" />
                                        Aperçu du Contrat
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'signature' ? 'active text-success' : 'text-secondary'}`}
                                        onClick={() => setActiveTab('signature')}
                                        style={{ fontWeight: 500, border: 'none', background: 'none', padding: '10px 20px' }}
                                    >
                                        <FaPalette className="me-2" />
                                        Signature DHS
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}

                    {/* Contenu principal — scrollable, occupe l'espace restant */}
                    <div className="p-4 pt-0" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {error && (
                            <Alert variant="danger" className="rounded-3 mb-4" style={{ flex: '0 0 auto' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <FaExclamationTriangle />
                                    <strong>Erreur de signature:</strong> {error}
                                </div>
                            </Alert>
                        )}

                        {success && (
                            <Alert variant="success" className="rounded-3 mb-4" style={{ flex: '0 0 auto' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <FaCheckCircle />
                                    <strong>Contrat employé activé avec succès!</strong>
                                    <span className="ms-2">Le contrat a été signé et activé.</span>
                                </div>
                            </Alert>
                        )}

                        {!success ? (
                            <>
                                {activeTab === 'preview' && (
                                    <div className="bg-light rounded-3 p-3" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                        {pdfUrl ? (
                                            <PdfReader url={pdfUrl} />
                                        ) : (
                                            <div className="text-center py-5">
                                                <Spinner animation="border" variant="success" />
                                                <p className="mt-3 text-muted">Chargement du contrat...</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'signature' && (
                                    <div className="bg-light rounded-3 p-3" style={{ flex: '1 1 auto', overflow: 'auto' }}>
                                        <div className="bg-white rounded-3 p-4">
                                            <div className="alert alert-info rounded-3 mb-4">
                                                <div className="d-flex gap-2">
                                                    <FaSignature size={20} />
                                                    <div>
                                                        <strong>Instructions :</strong> Signez dans la zone ci-dessous.
                                                        Votre signature sera apposée en bas à droite de la deuxième page
                                                        du contrat avec la date et l'heure actuelles.
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                ref={canvasWrapperRef}
                                                className="signature-canvas-wrapper rounded-3 mb-4"
                                                style={{
                                                    border: '2px dashed #ced4da',
                                                    padding: '8px',
                                                    backgroundColor: '#fff',
                                                    width: '100%',
                                                    height: '200px',
                                                    touchAction: 'none',
                                                    cursor: 'crosshair'
                                                }}
                                            >
                                                <SignatureCanvas
                                                    ref={sigCanvas}
                                                    canvasProps={{
                                                        style: {
                                                            width: '100%',
                                                            height: '100%',
                                                            display: 'block'
                                                        },
                                                        className: 'signature-canvas'
                                                    }}
                                                    minDistance={1}
                                                    throttle={0}
                                                    dotSize={2}
                                                    minWidth={1}
                                                    maxWidth={3}
                                                    velocityFilterWeight={0.5}
                                                />
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                                                <div className="bg-light rounded-pill px-3 py-2">
                                                    <FaCalendarAlt className="me-2 text-primary" />
                                                    <span className="small">Date et heure de signature:</span>
                                                    <strong className="ms-2">{formatDateTimeForDisplay()}</strong>
                                                </div>
                                                <button
                                                    className="btn btn-outline-secondary rounded-pill px-4 d-flex align-items-center gap-2"
                                                    onClick={resetSignature}
                                                >
                                                    <FaEraser /> Effacer la signature
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="bg-light rounded-3 p-3" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                <div className="bg-white rounded-3 p-3" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                    <h5 className="fw-bold text-success mb-3 d-flex align-items-center gap-2" style={{ flex: '0 0 auto' }}>
                                        <FaCheckCircle />
                                        Contrat employé final signé
                                    </h5>
                                    {modifiedPdfUrl ? (
                                        <PdfReader url={modifiedPdfUrl} />
                                    ) : (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" variant="success" />
                                            <p className="mt-3 text-muted">Génération du contrat final...</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer avec actions */}
                    <div className="modal-footer-fixed" style={{
                        flex: '0 0 auto', position: 'sticky', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                        padding: '16px 24px'
                    }}>
                        {!success ? (
                            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
                                {/* Bouton Annuler - caché sur mobile/tablette, rouge sans outline, désactivé pendant chargement */}
                                <button
                                    className="btn btn-danger rounded-pill px-4 d-flex align-items-center gap-2 d-none d-lg-inline-flex"
                                    onClick={onClose}
                                    disabled={isLoading}
                                    style={{
                                        border: 'none',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        opacity: isLoading ? 0.6 : 1,
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        flex: '0 0 auto'
                                    }}
                                >
                                    <FaTimes /> Annuler
                                </button>

                                {/* Boutons d'action principaux */}
                                <div className="d-flex gap-3" style={{ flex: '1 1 auto', justifyContent: 'flex-end' }}>
                                    {activeTab === 'preview' && (
                                        <button
                                            className="btn btn-primary rounded-pill px-4 d-flex align-items-center justify-content-center"
                                            onClick={() => setActiveTab('signature')}
                                            style={{
                                                flex: '1 1 auto',
                                                minWidth: '200px',
                                                maxWidth: '400px'
                                            }}
                                        >
                                            Continuer vers la signature
                                        </button>
                                    )}
                                    {activeTab === 'signature' && (
                                        <button
                                            className="btn btn-success rounded-pill px-4 d-flex align-items-center justify-content-center gap-2"
                                            onClick={handleSaveSignature}
                                            disabled={isLoading}
                                            style={{
                                                flex: '1 1 auto',
                                                minWidth: '200px',
                                                maxWidth: '400px'
                                            }}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" />
                                                    Activation en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <FaCheckCircle /> Activer le contrat
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex justify-content-end gap-3">
                                <button
                                    className="btn btn-outline-primary rounded-pill px-4 d-flex align-items-center gap-2"
                                    onClick={handleDownloadSignedPdf}
                                >
                                    <FaDownload /> Télécharger
                                </button>
                                <button
                                    className="btn btn-success rounded-pill px-4 d-flex align-items-center gap-2"
                                    onClick={onClose}
                                >
                                    <FaCheckCircle /> Terminer
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .admin-signature-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .nav-tabs .nav-link.active {
                    color: #28a745 !important;
                    border-bottom: 2px solid #28a745 !important;
                    background: transparent !important;
                }

                .signature-canvas {
                    background-color: white;
                    display: block;
                    width: 100% !important;
                    height: 100% !important;
                }

                .border-dashed {
                    border-style: dashed !important;
                }
            `}</style>
        </>
    );
};

export default AdminSignatureModal;